import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Warning as ConflictIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { mappingApi } from '../../services/api';
import CategoriesTab from './CategoriesTab';
import PatternsTab from './PatternsTab';
import ColorTypesPanel from './ColorTypesPanel';
import SampleProducts from './SampleProducts';
import ColorFamiliesPanel from './ColorFamiliesPanel';
import usePageLoadingEffect from '../../hooks/usePageLoadingEffect';

// Custom hook for debouncing a value
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

const statusColors = {
  pending_review: 'warning',
  conflict_multiple_suggestions: 'error',
  conflict_existing_mapping: 'error',
  accepted_pending_update: 'success',
  added_to_mappings: 'success',
  manually_rejected: 'default',
  superseded: 'default',
};

const attributeTypeColors = {
  color: { bgcolor: '#e3f2fd', color: '#1565c0' },      // Blue
};

export default function MappingReviewTable() {
  // Top-level tab state (0 = Color Mappings, 1 = Unmapped AI Values)
  const [topTab, setTopTab] = useState(0);
  // Sub-tab state within Color Mappings (0 = Pending, 1 = Existing, 2 = Generic)
  const [activeTab, setActiveTab] = useState(0);

  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  usePageLoadingEffect(loading);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Existing mappings state
  const [existingMappings, setExistingMappings] = useState([]);
  const [existingTotal, setExistingTotal] = useState(0);
  const [existingLoading, setExistingLoading] = useState(false);
  const [existingPage, setExistingPage] = useState(0);
  const [existingPageSize, setExistingPageSize] = useState(25);
  const [existingSearch, setExistingSearch] = useState('');

  // Debounce search to prevent too many API calls
  const debouncedExistingSearch = useDebounce(existingSearch, 300);

  // Filters
  const [status, setStatus] = useState('pending_review');

  // Selection
  const [selectedIds, setSelectedIds] = useState([]);

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Dialogs
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictDetails, setConflictDetails] = useState(null);
  const [batchConflictDialogOpen, setBatchConflictDialogOpen] = useState(false);
  const [batchConflicts, setBatchConflicts] = useState([]);

  // Manual mapping dialog
  const [manualMappingDialogOpen, setManualMappingDialogOpen] = useState(false);
  const [manualMappingRecord, setManualMappingRecord] = useState(null);
  const [normalizedValues, setNormalizedValues] = useState([]);
  const [normalizedValuesLoading, setNormalizedValuesLoading] = useState(false);
  const [normalizedValuesSearch, setNormalizedValuesSearch] = useState('');

  // Create new value state
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [newNormalizedValue, setNewNormalizedValue] = useState('');
  const [createNewConfirmOpen, setCreateNewConfirmOpen] = useState(false);

  // Override values (local state, maps record id -> override value)
  const [overrides, setOverrides] = useState({});

  // Search for reviews
  const [reviewSearch, setReviewSearch] = useState('');

  // Edit/delete existing mappings state
  const [editMappingDialogOpen, setEditMappingDialogOpen] = useState(false);
  const [editMappingRecord, setEditMappingRecord] = useState(null);
  const [editNormalizedValues, setEditNormalizedValues] = useState([]);
  const [editNormalizedValuesLoading, setEditNormalizedValuesLoading] = useState(false);
  const [editNormalizedValuesSearch, setEditNormalizedValuesSearch] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Generic values state
  const [genericValues, setGenericValues] = useState([]);
  const [genericValuesTotal, setGenericValuesTotal] = useState(0);
  const [genericValuesLoading, setGenericValuesLoading] = useState(false);
  const [addGenericDialogOpen, setAddGenericDialogOpen] = useState(false);
  const [newGenericValue, setNewGenericValue] = useState('');
  const [clearExisting, setClearExisting] = useState(false);
  const [affectedProductCount, setAffectedProductCount] = useState(null);
  const [totalProductCount, setTotalProductCount] = useState(null);
  const [affectedCountLoading, setAffectedCountLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [genericValueToDelete, setGenericValueToDelete] = useState(null);
  const [addGenericError, setAddGenericError] = useState(null);

  // Family state for category/color mappings
  const [familyOptions, setFamilyOptions] = useState([]);
  const [newMappingFamily, setNewMappingFamily] = useState('');

  const loadReviews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        status,
        attribute_type: 'color',
        limit: pageSize,
        offset: page * pageSize,
      };
      if (reviewSearch) params.search = reviewSearch;

      const data = await mappingApi.getReviews(params);
      setReviews(data.records || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [status, page, pageSize, reviewSearch]);

  const loadExistingMappings = useCallback(async () => {
    try {
      setExistingLoading(true);
      setError(null);

      const params = {
        limit: existingPageSize,
        offset: existingPage * existingPageSize,
      };
      if (debouncedExistingSearch) params.search = debouncedExistingSearch;

      const data = await mappingApi.getExistingMappings('color', params);
      setExistingMappings(data.mappings || []);
      setExistingTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setExistingLoading(false);
    }
  }, [existingPage, existingPageSize, debouncedExistingSearch]);

  const loadGenericValues = useCallback(async () => {
    try {
      setGenericValuesLoading(true);
      setError(null);

      const data = await mappingApi.getGenericValues('color');
      setGenericValues(data.generic_values || []);
      setGenericValuesTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenericValuesLoading(false);
    }
  }, []);

  // Load color family options
  const loadFamilies = useCallback(async () => {
    try {
      const data = await mappingApi.getFamilies('color');
      setFamilyOptions(data.families || []);
    } catch (err) {
      console.error('Failed to load families:', err);
      setFamilyOptions([]);
    }
  }, []);

  useEffect(() => {
    loadFamilies();
  }, [loadFamilies]);

  useEffect(() => {
    if (activeTab === 0) {
      loadReviews();
    } else if (activeTab === 1) {
      loadExistingMappings();
    } else if (activeTab === 2) {
      loadGenericValues();
    }
  }, [activeTab, loadReviews, loadExistingMappings, loadGenericValues]);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(reviews.map((r) => r.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleApprove = async () => {
    if (selectedIds.length === 0) return;

    try {
      setLoading(true);
      // Build updates - use override value if present, otherwise standard approve
      const updates = selectedIds.map((id) => {
        if (overrides[id] != null) {
          return { id, action: 'select_suggestion', selected_value: overrides[id] };
        }
        return { id, action: 'approve' };
      });
      const result = await mappingApi.batchUpdate(updates);

      if (result.success) {
        let message = `Approved ${result.approved} mapping(s)`;
        if (result.products_updated > 0) {
          message += `, updated ${result.products_updated} product(s)`;
        }
        if (result.auto_rejected > 0) {
          message += `. ${result.auto_rejected} conflicting suggestion(s) were automatically superseded.`;
        }
        setSuccess(message);
        // Clear overrides for approved records
        setOverrides((prev) => {
          const next = { ...prev };
          selectedIds.forEach((id) => delete next[id]);
          return next;
        });
        setSelectedIds([]);
        loadReviews();
      } else if (result.conflicts && result.conflicts.length > 0) {
        // Show conflict dialog
        setBatchConflicts(result.conflicts);
        setBatchConflictDialogOpen(true);
      } else {
        setError(result.error || 'Approval failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (selectedIds.length === 0) return;

    try {
      setLoading(true);
      const updates = selectedIds.map((id) => ({
        id,
        action: 'reject',
        reason: rejectReason,
      }));
      const result = await mappingApi.batchUpdate(updates);

      if (result.success) {
        setSuccess(`Rejected ${result.rejected} records`);
        setSelectedIds([]);
        setRejectDialogOpen(false);
        setRejectReason('');
        loadReviews();
      } else {
        setError(result.error || 'Rejection failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewConflict = async (reviewId) => {
    try {
      const details = await mappingApi.getConflictDetails(reviewId);
      setConflictDetails(details);
      setConflictDialogOpen(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSelectSuggestion = async (selectedValue) => {
    if (!conflictDetails) return;

    try {
      setLoading(true);
      const result = await mappingApi.batchUpdate([
        {
          id: conflictDetails.id,
          action: 'select_suggestion',
          selected_value: selectedValue,
        },
      ]);

      if (result.success) {
        // Show appropriate message based on what was selected
        const existingValue = conflictDetails.existing_mapping?.normalized_value;
        const isKeepingExisting = selectedValue === existingValue;
        let message = isKeepingExisting
          ? `Kept existing mapping: "${conflictDetails.raw_value}" → "${selectedValue}"`
          : `Updated mapping: "${conflictDetails.raw_value}" → "${selectedValue}"`;
        if (result.products_updated > 0) {
          message += ` (${result.products_updated} product(s) updated)`;
        }
        setSuccess(message);
        setConflictDialogOpen(false);
        setConflictDetails(null);
        loadReviews();
      } else {
        setError(result.error || 'Selection failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenManualMapping = async (row) => {
    setManualMappingRecord(row);
    setManualMappingDialogOpen(true);
    setNormalizedValuesSearch('');
    setShowCreateNew(false);
    setNewNormalizedValue('');
    setNormalizedValuesLoading(true);

    try {
      const params = {};
      if (row.brand) params.brand = row.brand;
      const values = await mappingApi.getNormalizedValues(row.attribute_type, params);
      setNormalizedValues(values);
    } catch (err) {
      console.error('Failed to load normalized values:', err);
      setNormalizedValues([]);
    } finally {
      setNormalizedValuesLoading(false);
    }
  };

  const handleSelectManualMapping = (normalizedValue) => {
    if (!manualMappingRecord) return;

    // Store override in local state (not calling API yet)
    setOverrides((prev) => ({
      ...prev,
      [manualMappingRecord.id]: normalizedValue,
    }));
    setSuccess(`Override set: "${manualMappingRecord.raw_value}" → "${normalizedValue}"`);
    setManualMappingDialogOpen(false);
    setManualMappingRecord(null);
  };

  const handleCreateNewValue = () => {
    if (!newNormalizedValue.trim()) return;
    setCreateNewConfirmOpen(true);
  };

  const handleConfirmCreateNew = async () => {
    if (!manualMappingRecord || !newNormalizedValue.trim()) return;

    try {
      setNormalizedValuesLoading(true);

      // Create the new mapping directly (with family for color)
      const family = newMappingFamily || null;
      const result = await mappingApi.createMapping(
        manualMappingRecord.attribute_type,
        manualMappingRecord.raw_value,
        newNormalizedValue.trim(),
        manualMappingRecord.brand,
        family
      );

      if (result.success) {
        const brandText = manualMappingRecord.brand ? ` for ${manualMappingRecord.brand}` : '';
        setSuccess(
          `Created new ${manualMappingRecord.attribute_type} mapping: "${manualMappingRecord.raw_value}" → "${newNormalizedValue.trim()}"${brandText}` +
          (result.is_new_normalized_value ? ' (new normalized value added to dictionary)' : '')
        );
        setCreateNewConfirmOpen(false);
        setManualMappingDialogOpen(false);
        setManualMappingRecord(null);
        setNewNormalizedValue('');
        setNewMappingFamily('');
        setShowCreateNew(false);
        loadReviews(); // Refresh the reviews
      } else {
        setError(result.error || 'Failed to create mapping');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setNormalizedValuesLoading(false);
    }
  };

  const filteredNormalizedValues = normalizedValues.filter((v) =>
    v.normalized_value.toLowerCase().includes(normalizedValuesSearch.toLowerCase())
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExistingChangePage = (event, newPage) => {
    setExistingPage(newPage);
  };

  const handleExistingChangeRowsPerPage = (event) => {
    setExistingPageSize(parseInt(event.target.value, 10));
    setExistingPage(0);
  };

  // Edit existing mapping handlers
  const handleOpenEditMapping = async (record) => {
    setEditMappingRecord(record);
    setEditMappingDialogOpen(true);
    setEditNormalizedValuesLoading(true);
    setEditNormalizedValuesSearch('');
    try {
      // Load ALL normalized values so user can move raw value to any
      const values = await mappingApi.getNormalizedValues('color', {});
      setEditNormalizedValues(values || []);
    } catch (err) {
      console.error('Failed to load normalized values:', err);
      setEditNormalizedValues([]);
    } finally {
      setEditNormalizedValuesLoading(false);
    }
  };

  const handleSelectEditMapping = async (newNormalizedValue) => {
    if (!editMappingRecord || !newNormalizedValue) return;

    setActionLoading(true);
    try {
      const result = await mappingApi.updateMapping(
        'color',
        editMappingRecord.id,
        editMappingRecord.raw_value,
        newNormalizedValue
      );

      if (result.success) {
        setSuccess(
          `Updated mapping: "${editMappingRecord.raw_value}" → "${newNormalizedValue}" (${result.products_updated} products updated)`
        );
        setEditMappingDialogOpen(false);
        setEditMappingRecord(null);
        loadExistingMappings();
      } else {
        setError(result.error || 'Failed to update mapping');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const filteredEditNormalizedValues = editNormalizedValues.filter((v) =>
    v.normalized_value.toLowerCase().includes(editNormalizedValuesSearch.toLowerCase())
  );

  // Generic values handlers
  const handleAddGenericValue = async () => {
    if (!newGenericValue.trim()) return;

    setActionLoading(true);
    setAddGenericError(null);
    try {
      const result = await mappingApi.addGenericValue(
        newGenericValue.trim().toLowerCase(),
        'color',
        clearExisting
      );

      if (result.success) {
        let message = `Added generic color value: "${newGenericValue.trim()}"`;
        if (clearExisting && result.products_cleared > 0) {
          message += ` (cleared ${result.products_cleared} normalized values)`;
        }
        setSuccess(message);
        setAddGenericDialogOpen(false);
        setNewGenericValue('');
        setClearExisting(false);
        setAffectedProductCount(null);
        setTotalProductCount(null);
        setAddGenericError(null);
        loadGenericValues();
      } else {
        setAddGenericError(result.error || 'Failed to add generic value');
      }
    } catch (err) {
      setAddGenericError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteGenericValue = async () => {
    if (!genericValueToDelete) return;

    setActionLoading(true);
    try {
      const result = await mappingApi.deleteGenericValue(genericValueToDelete.id);

      if (result.success) {
        setSuccess(`Deleted generic value: "${genericValueToDelete.raw_value}"`);
        setDeleteConfirmOpen(false);
        setGenericValueToDelete(null);
        loadGenericValues();
      } else {
        setError(result.error || 'Failed to delete generic value');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckAffectedCount = useCallback(async () => {
    if (!newGenericValue.trim()) {
      setAffectedProductCount(null);
      setTotalProductCount(null);
      return;
    }

    setAffectedCountLoading(true);
    try {
      const result = await mappingApi.getGenericValueAffectedCount(
        newGenericValue.trim().toLowerCase(),
        'color'
      );
      // would_be_cleared = products with existing normalized values that will be cleared
      // total_products = all products with this raw value
      setAffectedProductCount(result.would_be_cleared ?? result.affected_count ?? 0);
      setTotalProductCount(result.total_products ?? result.affected_count ?? 0);
    } catch (err) {
      console.error('Failed to get affected count:', err);
      setAffectedProductCount(null);
      setTotalProductCount(null);
    } finally {
      setAffectedCountLoading(false);
    }
  }, [newGenericValue]);

  // Load affected count when checkbox is enabled
  useEffect(() => {
    if (clearExisting && newGenericValue.trim()) {
      handleCheckAffectedCount();
    } else {
      setAffectedProductCount(null);
      setTotalProductCount(null);
    }
  }, [clearExisting, newGenericValue, handleCheckAffectedCount]);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Attribute Mapping
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Top-level Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={topTab} onChange={(e, v) => setTopTab(v)}>
          <Tab label="Colors" />
          <Tab label="Categories" />
          <Tab label="Patterns" />
        </Tabs>
      </Box>

      {/* Colors */}
      {topTab === 0 && (
        <>
          {/* Sub-tabs within Color Mappings */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="standard">
              <Tab label="Pending Reviews" />
              <Tab label="Existing Mappings" />
              <Tab label="Generic Values" />
              <Tab label="Color → Family" />
              <Tab label="Color Families" />
            </Tabs>
          </Box>

          {activeTab === 3 && <ColorTypesPanel />}
          {activeTab === 4 && <ColorFamiliesPanel />}

      {/* Filters - shared between Reviews and Existing Mappings sub-tabs */}
      {activeTab <= 1 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              {activeTab === 0 && (
                <FormControl size="small" sx={{ minWidth: { xs: '47%', sm: 200 } }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={status}
                    label="Status"
                    onChange={(e) => {
                      setStatus(e.target.value);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="pending_review">Pending Review</MenuItem>
                    <MenuItem value="conflict_multiple_suggestions">Conflict: Multiple</MenuItem>
                    <MenuItem value="conflict_existing_mapping">Conflict: Existing</MenuItem>
                    <MenuItem value="accepted_pending_update">Accepted (Pending Apply)</MenuItem>
                    <MenuItem value="added_to_mappings">Added to Mappings</MenuItem>
                    <MenuItem value="manually_rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
              )}

              {activeTab === 0 && (
                <TextField
                  size="small"
                  label="Search"
                  value={reviewSearch}
                  onChange={(e) => {
                    setReviewSearch(e.target.value);
                    setPage(0);
                  }}
                  placeholder="Search raw values..."
                  sx={{ minWidth: { xs: '47%', sm: 200 } }}
                />
              )}

              {activeTab === 1 && (
                <TextField
                  size="small"
                  label="Search"
                  value={existingSearch}
                  onChange={(e) => {
                    setExistingSearch(e.target.value);
                    setExistingPage(0);
                  }}
                  placeholder="Search raw or normalized..."
                  sx={{ minWidth: { xs: '47%', sm: 200 } }}
                />
              )}

              <Tooltip title="Refresh">
                <IconButton onClick={activeTab === 0 ? loadReviews : loadExistingMappings}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>

              {activeTab === 0 && (
                <>
                  <Box sx={{ flexGrow: 1 }} />

                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<ApproveIcon />}
                    onClick={handleApprove}
                    disabled={selectedIds.length === 0 || loading}
                  >
                    Approve ({selectedIds.length})
                  </Button>

                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<RejectIcon />}
                    onClick={() => setRejectDialogOpen(true)}
                    disabled={selectedIds.length === 0 || loading}
                  >
                    Reject ({selectedIds.length})
                  </Button>
                </>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Pending Reviews Tab */}
      {activeTab === 0 && (
        <Card>
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedIds.length > 0 && selectedIds.length < reviews.length}
                      checked={reviews.length > 0 && selectedIds.length === reviews.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Raw Value</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Suggested</TableCell>
                  <TableCell>Override</TableCell>
                  <TableCell>Confidence</TableCell>
                  <TableCell>Occurrences</TableCell>
                  <TableCell>Products</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : reviews.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  reviews.map((row) => {
                    const hasOverride = overrides[row.id] != null;
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        selected={selectedIds.includes(row.id)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.includes(row.id)}
                            onChange={() => handleSelectOne(row.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.attribute_type}
                            size="small"
                            sx={attributeTypeColors[row.attribute_type] || {}}
                          />
                        </TableCell>
                        <TableCell>{row.raw_value}</TableCell>
                        <TableCell>{row.brand || '-'}</TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              textDecoration: hasOverride ? 'line-through' : 'none',
                              color: hasOverride ? 'text.disabled' : 'text.primary',
                            }}
                          >
                            {row.suggested_normalized_value}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {hasOverride && (
                              <Chip
                                label={overrides[row.id]}
                                size="small"
                                color="info"
                                onDelete={() => {
                                  setOverrides((prev) => {
                                    const next = { ...prev };
                                    delete next[row.id];
                                    return next;
                                  });
                                }}
                              />
                            )}
                            <Tooltip title={hasOverride ? 'Change override' : 'Override suggestion'}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenManualMapping(row)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {row.confidence != null ? `${(row.confidence * 100).toFixed(0)}%` : '-'}
                        </TableCell>
                        <TableCell>{row.occurrence_count}</TableCell>
                        <TableCell><SampleProducts products={row.sample_products} /></TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                              label={row.status.replace(/_/g, ' ')}
                              color={statusColors[row.status] || 'default'}
                              size="small"
                            />
                            {row.status.startsWith('conflict_') && (
                              <Tooltip title="Resolve Conflict">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewConflict(row.id)}
                                >
                                  <ConflictIcon color="warning" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={total}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      )}

      {/* Existing Color Mappings Tab */}
      {activeTab === 1 && (
        <Card>
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Raw Value</TableCell>
                  <TableCell>Normalized Value</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Family</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {existingLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : existingMappings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      No existing mappings found
                    </TableCell>
                  </TableRow>
                ) : (
                  existingMappings.map((row) => (
                    <TableRow key={`${row.id}_${row.raw_value}`} hover>
                      <TableCell>{row.raw_value}</TableCell>
                      <TableCell>
                        <Chip label={row.normalized_value} size="small" color="primary" variant="outlined" />
                      </TableCell>
                      <TableCell>{row.brand || '-'}</TableCell>
                      <TableCell>
                        {row.family ? (
                          <Chip label={row.family} size="small" variant="outlined" color="secondary" />
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.created_at ? new Date(row.created_at).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit mapping">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditMapping(row)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={existingTotal}
            rowsPerPage={existingPageSize}
            page={existingPage}
            onPageChange={handleExistingChangePage}
            onRowsPerPageChange={handleExistingChangeRowsPerPage}
          />
        </Card>
      )}

      {/* Generic Color Values Tab */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <Tooltip title="Refresh">
                <IconButton onClick={loadGenericValues}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>

              <Box sx={{ flexGrow: 1 }} />

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddGenericDialogOpen(true)}
              >
                Add Generic Value
              </Button>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              Generic values are raw color values that are too vague (like "default", "unknown", "n/a") and should trigger AI normalization instead of direct mapping.
            </Alert>

            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Raw Value</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {genericValuesLoading ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : genericValues.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                        No generic values found
                      </TableCell>
                    </TableRow>
                  ) : (
                    genericValues.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.raw_value}</TableCell>
                        <TableCell>
                          {row.created_at ? new Date(row.created_at).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Delete generic value">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setGenericValueToDelete(row);
                                setDeleteConfirmOpen(true);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Total: {genericValuesTotal} generic value(s)
            </Typography>
          </CardContent>
        </Card>
      )}

        </>
      )}

      {/* Unmapped AI Values Tab */}
      {topTab === 1 && <CategoriesTab />}

      {topTab === 2 && <PatternsTab />}

      {/* Add Generic Value Dialog */}
      <Dialog
        open={addGenericDialogOpen}
        onClose={() => {
          setAddGenericDialogOpen(false);
          setNewGenericValue('');
          setClearExisting(false);
          setAffectedProductCount(null);
          setTotalProductCount(null);
          setAddGenericError(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Generic Value</DialogTitle>
        <DialogContent>
          {addGenericError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAddGenericError(null)}>
              {addGenericError}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Generic values are raw color values that are too vague to map directly and should trigger AI normalization.
          </Typography>

          <TextField
            fullWidth
            label="Raw Color Value"
            value={newGenericValue}
            onChange={(e) => setNewGenericValue(e.target.value)}
            placeholder="e.g., default, unknown, n/a, other"
            sx={{ mb: 2 }}
            autoFocus
          />

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={
              <Checkbox
                checked={clearExisting}
                onChange={(e) => setClearExisting(e.target.checked)}
              />
            }
            label="Clear existing normalized values for products with this raw value"
          />

          {clearExisting && (
            <Alert severity={totalProductCount === 0 ? 'info' : affectedProductCount === 0 ? 'success' : 'warning'} sx={{ mt: 1 }}>
              {affectedCountLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <span>Checking affected products...</span>
                </Box>
              ) : totalProductCount !== null ? (
                totalProductCount === 0 ? (
                  'No active products have this raw value. The generic value will apply to future products.'
                ) : affectedProductCount === 0 ? (
                  <>
                    <strong>{totalProductCount}</strong> active product(s) have this raw value (none have been normalized yet).
                    These will be normalized by AI on the next pipeline run.
                  </>
                ) : (
                  <>
                    <strong>{totalProductCount}</strong> active product(s) have this raw value.
                    <strong> {affectedProductCount}</strong> of them have existing normalized values that will be cleared
                    and re-normalized on the next pipeline run.
                  </>
                )
              ) : (
                'Enter a raw value to see affected products.'
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddGenericDialogOpen(false);
              setNewGenericValue('');

              setClearExisting(false);
              setAffectedProductCount(null);
              setTotalProductCount(null);
              setAddGenericError(null);
            }}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddGenericValue}
            variant="contained"
            disabled={!newGenericValue.trim() || actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Generic Value Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setGenericValueToDelete(null);
        }}
      >
        <DialogTitle>Delete Generic Value</DialogTitle>
        <DialogContent>
          {genericValueToDelete && (
            <Typography>
              Are you sure you want to delete the generic value "<strong>{genericValueToDelete.raw_value}</strong>" for {genericValueToDelete.attribute_type}?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              setGenericValueToDelete(null);
            }}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteGenericValue}
            color="error"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
        <DialogTitle>Reject Mappings</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Rejecting {selectedIds.length} mapping(s). Optionally provide a reason:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReject} color="error" variant="contained">
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Conflict Resolution Dialog */}
      <Dialog
        open={conflictDialogOpen}
        onClose={() => {
          setConflictDialogOpen(false);
          setConflictDetails(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Resolve Conflict</DialogTitle>
        <DialogContent>
          {conflictDetails && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Raw Value: <strong>{conflictDetails.raw_value}</strong>
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Brand: <strong>{conflictDetails.brand || 'N/A'}</strong>
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Attribute Type: <Chip label={conflictDetails.attribute_type} size="small" />
              </Typography>

              {conflictDetails.status === 'conflict_multiple_suggestions' && (
                <>
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    Select a suggestion:
                  </Typography>
                  <List>
                    {conflictDetails.all_suggestions?.map((suggestion, index) => (
                      <ListItem key={index} disablePadding>
                        <ListItemButton onClick={() => handleSelectSuggestion(suggestion.value)}>
                          <ListItemText
                            primary={suggestion.value}
                            secondary={`Confidence: ${(suggestion.confidence * 100).toFixed(0)}%`}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {conflictDetails.status === 'conflict_existing_mapping' && (
                <>
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    This value already has an existing mapping.
                  </Alert>
                  {conflictDetails.existing_mapping && (
                    <Typography sx={{ mt: 1 }}>
                      Existing: <strong>{conflictDetails.existing_mapping.normalized_value}</strong>
                    </Typography>
                  )}
                  <Typography sx={{ mt: 1 }}>
                    Suggested: <strong>{conflictDetails.suggested_normalized_value}</strong>
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() => handleSelectSuggestion(conflictDetails.existing_mapping?.normalized_value)}
                    >
                      Keep Existing
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => handleSelectSuggestion(conflictDetails.suggested_normalized_value)}
                    >
                      Use New Suggestion
                    </Button>
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConflictDialogOpen(false);
              setConflictDetails(null);
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Conflict Dialog - shown when trying to approve conflicting records */}
      <Dialog
        open={batchConflictDialogOpen}
        onClose={() => {
          setBatchConflictDialogOpen(false);
          setBatchConflicts([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Conflicting Selections Detected</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            You've selected multiple records with different suggested values for the same raw value.
            Please deselect conflicting records and approve them one at a time.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When you approve one suggestion, any other pending suggestions for the same raw value
            will be automatically marked as superseded.
          </Typography>
          {batchConflicts.map((conflict, index) => (
            <Card key={index} variant="outlined" sx={{ mb: 2, p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Type:</strong> {conflict.attribute_type} |{' '}
                <strong>Raw Value:</strong> "{conflict.raw_value}"
                {conflict.brand && (
                  <> | <strong>Brand:</strong> {conflict.brand}</>
                )}
              </Typography>
              <Typography variant="body2" color="error">
                <strong>Conflicting suggestions:</strong>
              </Typography>
              <Box sx={{ pl: 2, mt: 1 }}>
                {conflict.conflicting_values?.map((value, i) => (
                  <Chip
                    key={i}
                    label={value}
                    size="small"
                    color="warning"
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </Box>
            </Card>
          ))}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBatchConflictDialogOpen(false);
              setBatchConflicts([]);
            }}
            variant="contained"
          >
            OK, I'll Fix My Selection
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Mapping Dialog */}
      <Dialog
        open={manualMappingDialogOpen}
        onClose={() => {
          setManualMappingDialogOpen(false);
          setManualMappingRecord(null);
          setNormalizedValues([]);
          setNormalizedValuesSearch('');
          setShowCreateNew(false);
          setNewNormalizedValue('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select or Create Mapping</DialogTitle>
        <DialogContent>
          {manualMappingRecord && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Raw Value: <strong>{manualMappingRecord.raw_value}</strong>
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Type: <Chip label={manualMappingRecord.attribute_type} size="small" />
              </Typography>
              {manualMappingRecord.brand && (
                <Typography variant="subtitle2" gutterBottom>
                  Brand: <strong>{manualMappingRecord.brand}</strong>
                </Typography>
              )}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                AI Suggestion: {manualMappingRecord.suggested_normalized_value}
              </Typography>

              <Divider sx={{ my: 2 }} />

              {!showCreateNew ? (
                <>
                  <TextField
                    fullWidth
                    label="Search existing normalized values"
                    value={normalizedValuesSearch}
                    onChange={(e) => setNormalizedValuesSearch(e.target.value)}
                    sx={{ mb: 1 }}
                    size="small"
                  />

                  {normalizedValuesLoading ? (
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <List sx={{ maxHeight: 250, overflow: 'auto' }}>
                      {filteredNormalizedValues.length === 0 ? (
                        <ListItem>
                          <ListItemText
                            primary="No normalized values found"
                            secondary={manualMappingRecord.brand ? `for brand "${manualMappingRecord.brand}"` : ''}
                          />
                        </ListItem>
                      ) : (
                        filteredNormalizedValues.map((item, index) => (
                          <ListItem key={index} disablePadding>
                            <ListItemButton onClick={() => handleSelectManualMapping(item.normalized_value)}>
                              <ListItemText
                                primary={item.normalized_value}
                                secondary={`${item.usage_count} existing mapping(s)`}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))
                      )}
                    </List>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setShowCreateNew(true)}
                    fullWidth
                  >
                    Create New Normalized Value
                  </Button>
                </>
              ) : (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    You are creating a NEW normalized {manualMappingRecord.attribute_type} value
                    {manualMappingRecord.brand && (
                      <strong> for {manualMappingRecord.brand}</strong>
                    )}. This will be added to the dictionary.
                  </Alert>

                  <TextField
                    fullWidth
                    label={`New normalized ${manualMappingRecord.attribute_type} value`}
                    value={newNormalizedValue}
                    onChange={(e) => setNewNormalizedValue(e.target.value)}
                    placeholder={`e.g., ${manualMappingRecord.attribute_type === 'color' ? 'teal' : manualMappingRecord.attribute_type === 'category' ? 'dresses' : 'floral'}`}
                    sx={{ mb: 2 }}
                    autoFocus
                  />

                  {/* Family selector for color */}
                  {familyOptions.length > 0 && (
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>Family (for matching compatibility)</InputLabel>
                      <Select
                        value={newMappingFamily}
                        label="Family (for matching compatibility)"
                        onChange={(e) => setNewMappingFamily(e.target.value)}
                      >
                        <MenuItem value="">
                          <em>Select a family...</em>
                        </MenuItem>
                        {familyOptions.map((f) => (
                          <MenuItem key={f} value={f}>{f}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setShowCreateNew(false);
                        setNewNormalizedValue('');
                        setNewMappingFamily('');
                      }}
                    >
                      Back to Search
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleCreateNewValue}
                      disabled={!newNormalizedValue.trim()}
                    >
                      Create & Map
                    </Button>
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setManualMappingDialogOpen(false);
              setManualMappingRecord(null);
              setNormalizedValues([]);
              setNormalizedValuesSearch('');
              setShowCreateNew(false);
              setNewNormalizedValue('');
              setNewMappingFamily('');
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create New Value Confirmation Dialog */}
      <Dialog
        open={createNewConfirmOpen}
        onClose={() => setCreateNewConfirmOpen(false)}
      >
        <DialogTitle>Confirm New Normalized Value</DialogTitle>
        <DialogContent>
          {manualMappingRecord && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                You are about to create a <strong>NEW</strong> normalized {manualMappingRecord.attribute_type} value.
              </Alert>

              <Typography variant="body1" gutterBottom>
                <strong>Mapping:</strong> "{manualMappingRecord.raw_value}" → "<strong>{newNormalizedValue.trim()}</strong>"
              </Typography>

              {manualMappingRecord.brand && (
                <Typography variant="body1" gutterBottom>
                  <strong>Brand:</strong> {manualMappingRecord.brand}
                </Typography>
              )}

              {newMappingFamily && (
                <Typography variant="body1" gutterBottom>
                  <strong>Family:</strong> {newMappingFamily}
                </Typography>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                The value "<strong>{newNormalizedValue.trim()}</strong>" will be added to the {manualMappingRecord.attribute_type} dictionary
                {manualMappingRecord.brand ? ` for ${manualMappingRecord.brand}` : ''}.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateNewConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmCreateNew}
            variant="contained"
            color="primary"
            disabled={normalizedValuesLoading}
          >
            {normalizedValuesLoading ? <CircularProgress size={20} /> : 'Confirm & Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Existing Mapping Dialog */}
      <Dialog
        open={editMappingDialogOpen}
        onClose={() => {
          setEditMappingDialogOpen(false);
          setEditMappingRecord(null);
          setEditNormalizedValues([]);
          setEditNormalizedValuesSearch('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Mapping</DialogTitle>
        <DialogContent>
          {editMappingRecord && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Raw Value: <strong>{editMappingRecord.raw_value}</strong>
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Current Normalized Value: <Chip label={editMappingRecord.normalized_value} size="small" color="primary" variant="outlined" />
              </Typography>
              {editMappingRecord.brand && (
                <Typography variant="subtitle2" gutterBottom>
                  Brand: <strong>{editMappingRecord.brand}</strong>
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom>
                Select a new normalized value:
              </Typography>

              <TextField
                fullWidth
                label="Search normalized values"
                value={editNormalizedValuesSearch}
                onChange={(e) => setEditNormalizedValuesSearch(e.target.value)}
                sx={{ mb: 1 }}
                size="small"
              />

              {editNormalizedValuesLoading ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <List sx={{ maxHeight: 250, overflow: 'auto' }}>
                  {filteredEditNormalizedValues.length === 0 ? (
                    <ListItem>
                      <ListItemText primary="No normalized values found" />
                    </ListItem>
                  ) : (
                    filteredEditNormalizedValues
                      .filter((item) => item.normalized_value !== editMappingRecord.normalized_value)
                      .map((item, index) => (
                        <ListItem key={index} disablePadding>
                          <ListItemButton
                            onClick={() => handleSelectEditMapping(item.normalized_value)}
                            disabled={actionLoading}
                          >
                            <ListItemText
                              primary={item.normalized_value}
                              secondary={`${item.usage_count} existing mapping(s)`}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))
                  )}
                </List>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditMappingDialogOpen(false);
              setEditMappingRecord(null);
              setEditNormalizedValues([]);
              setEditNormalizedValuesSearch('');
            }}
            disabled={actionLoading}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
